import { z } from 'zod';

const LoginDataSchema = z.object({
  customerId: z.number(),
  shortId: z.string(),
  username: z.string(),
  lastLogintime: z.number().nullable(),
  failedCvvCount: z.number(),
  cvvFailureMax: z.number(),
  inMaintenanceMode: z.boolean(),
  currencyCd: z.string(),
  locale: z.string(),
});

// https://github.roving.com/ES/billingsvc-rest-client/blob/integration/billingsvc-rest-client/src/main/java/com/constantcontact/billing/products/dto/BillingType.java#L10
export enum BillingType {
  REGULAR_BILLED = 'R',
  PARTNER_BILLED = 'P',
  SINGLE_BILLED = 'S',
  NO_BILLING_SYSTEM = 'N',
}

const BillingTypeSchema = z.object({
  name: z.enum([
    BillingType.NO_BILLING_SYSTEM,
    BillingType.PARTNER_BILLED,
    BillingType.SINGLE_BILLED,
    BillingType.REGULAR_BILLED,
  ]),
});

const SoProductTypeSchema = z.object({
  id: z.number(),
  mainProduct: z.boolean(),
  addon: z.boolean(),
  packageProduct: z.boolean(),
  billingBasedOnSubscribers: z.boolean(),
  billingBasedOnResponses: z.boolean(),
  billingBasedOnEvents: z.boolean(),
  billingBasedOnSocialCount: z.boolean(),
  billingBasedOnFees: z.boolean(),
  billingBasedOnMembers: z.boolean(),
  billingBasedOnListings: z.boolean(),
  usageBased: z.boolean(),
  storageBased: z.boolean(),
  chargedOverages: z.boolean(),
  taxable: z.boolean(),
  productName: z.string(),
  productDescription: z.string(),
  requiredProducts: z.unknown().nullable(),
  dependentProducts: z.unknown().nullable(),
  productOrder: z.number(),
  jbillingId: z.number(),
  showPricingLevels: z.boolean(),
  trialAllowed: z.boolean(),
  anotherProductRequired: z.boolean(),
});

const StatusSchema = z.object({
  status: z.string(),
  description: z.string(),
});

const TrialEndReasonSchema = z.object({
  id: z.number(),
  description: z.string(),
});

const ProductSchema = z.object({
  customerId: z.number(),
  soProductType: SoProductTypeSchema,
  status: StatusSchema,
  disabled: z.boolean(),
  cancelReason: z
    .object({
      id: z.number(),
      description: z.string(),
    })
    .nullable(),
  cancelTime: z.number().nullable(),
  disableReasons: z.array(z.unknown()),
  suspendTime: z.number().nullable(),
  pendingCancelReason: z.string().nullable(),
  pendingCancelDate: z.string().nullable(),
  pendingSuspendDate: z.string().nullable(),
  trialExpirationDate: z.string(),
  trialEndDate: z.string().nullable(),
  trialEndReason: TrialEndReasonSchema.nullable(),
  bundleTrialStartTime: z.number().nullable(),
  firstPaymentTime: z.number().nullable(),
  firstBillableTime: z.number().nullable(),
  insertTime: z.number(),
  lastUpdateTime: z.number(),
  lastUpdateBy: z.string(),
  highwatermark: z.number(),
});

const ProductSummarySchema = z.object({
  customerId: z.number(),
  soProductType: SoProductTypeSchema,
  status: StatusSchema,
  insertTime: z.number(),
});

const AvailableProductSchema = z.object({
  id: z.number(),
  mainProduct: z.boolean(),
  addon: z.boolean(),
  packageProduct: z.boolean(),
  billingBasedOnSubscribers: z.boolean(),
  billingBasedOnResponses: z.boolean(),
  billingBasedOnEvents: z.boolean(),
  billingBasedOnSocialCount: z.boolean(),
  billingBasedOnFees: z.boolean(),
  billingBasedOnMembers: z.boolean(),
  billingBasedOnListings: z.boolean(),
  usageBased: z.boolean(),
  storageBased: z.boolean(),
  chargedOverages: z.boolean(),
  taxable: z.boolean(),
  productName: z.string(),
  productDescription: z.string(),
  requiredProducts: z.unknown().nullable(),
  dependentProducts: z.unknown().nullable(),
  productOrder: z.number(),
  jbillingId: z.number(),
  showPricingLevels: z.boolean(),
  trialAllowed: z.boolean(),
  anotherProductRequired: z.boolean(),
});

export const BillingAccountExtendedSchema = z.object({
  customerId: z.number(),
  loginData: LoginDataSchema,
  billingType: BillingTypeSchema,
  accountCancelTime: z.number().nullable(),
  accountStatusCodeIncludingTrial: z.string(),
  accountStatusStringIncludingTrial: z.string(),
  currencyCd: z.string(),
  currencyLanguage: z.string(),
  currencyCountry: z.string(),
  disableReasons: z.array(z.unknown()),
  firstPaymentTime: z.number().nullable(),
  scheduledCancelDate: z.string().nullable(),
  scheduledCancelReason: z.string().nullable(),
  scheduledSuspendDate: z.string().nullable(),
  statusString: z.string(),
  suspendTime: z.number().nullable(),
  username: z.string(),
  allowedToReactivate: z.boolean(),
  callToReactivate: z.boolean(),
  canceled: z.boolean(),
  disabledForPayment: z.boolean(),
  disabledForCompliance: z.boolean(),
  inTrial: z.boolean(),
  loginAllowed: z.boolean(),
  loginAllowedToHomePage: z.boolean(),
  newAccount: z.boolean(),
  noBillingSystem: z.boolean(),
  open: z.boolean(),
  partnerBilling: z.boolean(),
  payingCustomer: z.boolean(),
  scheduledToBeCanceled: z.boolean(),
  scheduledToBeSuspended: z.boolean(),
  singleBilled: z.boolean(),
  suspended: z.boolean(),
  bridgeCustomer: z.boolean(),
  managed: z.boolean(),
  requestManaged: z.boolean(),
  availableProductList: z.array(AvailableProductSchema),
  activeProducts: z.array(ProductSchema).nullable(),
  allProducts: z.array(ProductSchema),
  payingProducts: z.array(ProductSchema),
  allProductsSummary: z.array(ProductSummarySchema),
  payingAllProductIds: z.array(z.string()),
  openProducts: z.array(ProductSchema),
});

export type BillingAccountExtendedResponse = z.infer<typeof BillingAccountExtendedSchema>;
